/** Pointer-based drag for fighters: drag onto the wild card to tame, onto an enemy to raid. Works with mouse and touch. */
import { useCallback, useRef, useState } from 'react';

export interface DragState { slot: number; x: number; y: number; over: string | null; startX: number; startY: number; active: boolean }

export function useDrag(onDrop: (slot: number, target: string) => void, canDrag: (slot: number) => boolean) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const ref = useRef<DragState | null>(null);
  const set = (d: DragState | null) => { ref.current = d; setDrag(d); };

  const onPointerDown = useCallback((slot: number) => (e: React.PointerEvent) => {
    if (!canDrag(slot) || e.button > 0) return;
    set({ slot, x: e.clientX, y: e.clientY, over: null, startX: e.clientX, startY: e.clientY, active: false });
  }, [canDrag]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const d = ref.current; if (!d) return;
    const active = d.active || Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > 8;
    if (!active) return;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const zone = el?.closest<HTMLElement>('[data-drop]');
    set({ ...d, x: e.clientX, y: e.clientY, over: zone?.dataset.drop ?? null, active: true });
  }, []);

  const onPointerUp = useCallback(() => {
    const d = ref.current; if (!d) return;
    set(null);
    if (d.active && d.over) onDrop(d.slot, d.over);
  }, [onDrop]);

  return { drag: drag?.active ? drag : null, handlers: { onPointerMove, onPointerUp, onPointerCancel: onPointerUp }, onPointerDown };
}
